/**
 * @nanokart/tryon-studio-sdk
 * API Client Configuration
 */

export interface ApiClientConfig {
  baseUrl: string;
  headers: Record<string, string>;
  timeout?: number;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  ok: boolean;
}

export interface ApiError {
  code: string;
  message: string;
  status: number;
  details?: Record<string, unknown>;
}

/**
 * Create a configured fetch wrapper with error handling
 */
export function createApiClient(config: ApiClientConfig) {
  const { baseUrl, headers: defaultHeaders, timeout = 30000 } = config;

  async function request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${baseUrl}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const headers = {
      ...defaultHeaders,
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      let data: T;
      const contentType = response.headers.get('content-type');
      
      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        data = (await response.text()) as unknown as T;
      }

      if (!response.ok) {
        // Handle error response - detail can be string or object
        let errorMessage = response.statusText;
        const detail = (data as { detail?: string | { message?: string } })?.detail;
        
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail && typeof detail === 'object') {
          // Handle structured error: { message, error_code, detail }
          errorMessage = (detail as { message?: string }).message || response.statusText;
        }
        
        const error: ApiError = {
          code: `api/${response.status}`,
          message: errorMessage,
          status: response.status,
          details: data as Record<string, unknown>,
        };
        throw error;
      }

      return {
        data,
        status: response.status,
        ok: true,
      };
    } catch (err) {
      clearTimeout(timeoutId);

      if (err instanceof Error && err.name === 'AbortError') {
        const error: ApiError = {
          code: 'api/timeout',
          message: 'Request timed out',
          status: 408,
        };
        throw error;
      }

      // Re-throw ApiError as-is
      if ((err as ApiError).code?.startsWith('api/')) {
        throw err;
      }

      // Network or other error
      const error: ApiError = {
        code: 'api/network-error',
        message: err instanceof Error ? err.message : 'Network error',
        status: 0,
      };
      throw error;
    }
  }

  return {
    get: <T>(endpoint: string, params?: Record<string, string>) => {
      const url = params 
        ? `${endpoint}?${new URLSearchParams(params).toString()}`
        : endpoint;
      return request<T>(url, { method: 'GET' });
    },

    post: <T>(endpoint: string, body?: unknown) => {
      return request<T>(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
    },

    postForm: <T>(endpoint: string, formData: FormData) => {
      return request<T>(endpoint, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type - let browser handle multipart boundary
      });
    },

    patch: <T>(endpoint: string, body?: unknown) => {
      return request<T>(endpoint, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
    },

    delete: <T>(endpoint: string) => {
      return request<T>(endpoint, { method: 'DELETE' });
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
